import { useRef, useEffect, useState, useCallback } from 'react'
import {
  Maximize2,
  Minimize2,
  MicOff,
  VideoOff,
  Pin,
  PinOff,
  Grid,
  Monitor,
  X,
  Video
} from 'lucide-react'
import { CallState } from '../../services/MediaService'
import { CallControls } from './CallControls'
// Avatar component removed - using plain img tag like IncomingCallNotification

interface VideoCallUIProps {
  callState: CallState
  localStream?: MediaStream | null
  remoteStream?: MediaStream | null
  onEndCall: () => void
  onToggleMute: () => void
  onToggleVideo: () => void
  onToggleScreen: () => void
  isMuted?: boolean
  isVideoEnabled?: boolean
  isScreenSharing?: boolean
  connectionQuality?: 'excellent' | 'good' | 'poor' | 'unknown'
  // Video quality and degradation support
  videoQuality?: 'excellent' | 'good' | 'poor' | 'failed'
  isDegraded?: boolean
  onUpgradeToVideo?: () => void
}

export function VideoCallUI({
  callState,
  localStream,
  remoteStream,
  onEndCall,
  onToggleMute,
  onToggleVideo,
  onToggleScreen,
  isMuted = false,
  isVideoEnabled = true,
  isScreenSharing = false,
  connectionQuality = 'unknown',
  videoQuality = 'excellent',
  isDegraded = false,
  onUpgradeToVideo,
}: VideoCallUIProps) {
  const localVideoRef = useRef<HTMLVideoElement>(null)
  const remoteVideoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isPinned, setIsPinned] = useState(false)
  const [layout, setLayout] = useState<'grid' | 'sidebar' | 'pip'>('sidebar')

  // Video interaction state for mobile autoplay policies
  const [needsVideoInteraction] = useState(false)
  const [videoInteractionMessage] = useState('Tap to start video')

  // CRITICAL FIX: Re-attach video streams when layout changes to prevent black screens
  const reattachVideoStreams = useCallback(() => {
    console.log('🔄 Re-attaching video streams due to layout change')

    // Force re-attachment of local video with enhanced error recovery
    if (localVideoRef.current && localStream) {
      const localVideo = localVideoRef.current
      try {
        // Clear the current stream
        localVideo.srcObject = null

        // Re-attach with delay to allow DOM to stabilize
        setTimeout(() => {
          if (localVideoRef.current && localStream) {
            localVideo.srcObject = localStream
            localVideo.muted = true
            localVideo.playsInline = true
            localVideo.autoplay = true

            // Enhanced playback with fallback
            localVideo.play().catch(e => {
              console.warn('Local video replay failed, attempting recovery:', e)
              setTimeout(() => {
                if (localVideoRef.current) {
                  localVideoRef.current.play().catch(e2 =>
                    console.error('Local video recovery failed:', e2)
                  )
                }
              }, 200)
            })
          }
        }, 50)
      } catch (error) {
        console.error('Local video reattachment failed:', error)
      }
    }

    // Force re-attachment of remote video with enhanced error recovery
    if (remoteVideoRef.current && remoteStream) {
      const remoteVideo = remoteVideoRef.current
      try {
        // Clear the current stream
        remoteVideo.srcObject = null

        // Re-attach with delay to allow DOM to stabilize
        setTimeout(() => {
          if (remoteVideoRef.current && remoteStream) {
            remoteVideo.srcObject = remoteStream
            remoteVideo.muted = true
            remoteVideo.playsInline = true
            remoteVideo.autoplay = true

            // Enhanced playback with fallback
            remoteVideo.play().catch(e => {
              console.warn('Remote video replay failed, attempting recovery:', e)
              setTimeout(() => {
                if (remoteVideoRef.current) {
                  remoteVideoRef.current.play().catch(e2 =>
                    console.error('Remote video recovery failed:', e2)
                  )
                }
              }, 200)
            })
          }
        }, 50)
      } catch (error) {
        console.error('Remote video reattachment failed:', error)
      }
    }
  }, [localStream, remoteStream])

  // CRITICAL FIX: Re-attach video streams when layout changes
  useEffect(() => {
    console.log('🔄 Layout changed, re-attaching video streams')
    reattachVideoStreams()
  }, [layout, reattachVideoStreams])

  // Setup local video stream with iOS Safari and desktop support
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      try {
        const videoElement = localVideoRef.current
        const videoTracks = localStream.getVideoTracks()

        // CRITICAL FIX: Validate video tracks before assignment
        if (videoTracks.length === 0) {
          console.warn('⚠️ No video tracks in local stream')
          return
        }

        const videoTrack = videoTracks[0]
        if (videoTrack && videoTrack.readyState !== 'live') {
          console.warn('⚠️ Video track not live:', videoTrack.readyState)
          return
        }

        // iOS Safari Detection
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

        console.log('🔍 LOCAL VIDEO DEBUG:', {
          isIOSSafari,
          isMobile,
          streamTracks: localStream.getTracks().length,
          videoTracks: videoTracks.length,
          trackReadyState: videoTrack?.readyState,
          userAgent: navigator.userAgent.substring(0, 50)
        })

        // Enhanced cross-platform configuration
        videoElement.srcObject = localStream
        videoElement.muted = true // Prevent echo
        videoElement.playsInline = true

        // DESKTOP & MOBILE ENHANCEMENT: Additional properties for better compatibility
        videoElement.autoplay = true
        videoElement.controls = false
        videoElement.disablePictureInPicture = true

        // CRITICAL iOS FIX: Enhanced iOS-specific local video attributes
        if (isIOSSafari) {
          videoElement.setAttribute('playsinline', 'true')
          videoElement.setAttribute('webkit-playsinline', 'true')
          videoElement.setAttribute('muted', 'true')
          videoElement.setAttribute('preload', 'metadata')
          videoElement.setAttribute('poster', 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K') // Transparent poster to avoid blank screen
          videoElement.style.objectFit = 'cover'
          videoElement.style.transform = 'translateZ(0)' // Hardware acceleration
          videoElement.style.willChange = 'transform' // iOS optimization hint
          videoElement.style.webkitTransform = 'translateZ(0)' // WebKit specific
          console.log('📱 iOS Safari enhanced local video attributes applied')
        }

        // CRITICAL FIX: Multi-strategy autoplay handling
        const playVideo = async () => {
          try {
            await videoElement.play()
            console.log('✅ Local video playing successfully')
          } catch (error) {
            console.warn('⚠️ Local video autoplay failed, trying fallbacks:', error)

            // Desktop-specific fallback strategies
            setTimeout(async () => {
              try {
                await videoElement.play()
                console.log('✅ Local video playing after timeout')
              } catch (retryError) {
                console.error('❌ Local video play failed completely:', retryError)
              }
            }, 100)
          }
        }

        // Start playback
        playVideo()

        // Monitor video dimensions for debugging
        videoElement.addEventListener('loadedmetadata', () => {
          console.log('📹 Local video metadata loaded:', {
            videoWidth: videoElement.videoWidth,
            videoHeight: videoElement.videoHeight,
            duration: videoElement.duration
          })
        })

        console.log('✅ Local video stream attached with enhanced desktop support')

        // EMERGENCY DEBUG: Check if video is actually playing
        setTimeout(() => {
          console.log('🔍 LOCAL VIDEO STATUS CHECK:', {
            paused: videoElement.paused,
            ended: videoElement.ended,
            readyState: videoElement.readyState,
            videoWidth: videoElement.videoWidth,
            videoHeight: videoElement.videoHeight,
            currentTime: videoElement.currentTime,
            srcObject: !!videoElement.srcObject
          })
        }, 2000)
      } catch (error) {
        console.error('❌ Failed to attach local video stream:', error)
      }
    }

    return () => {
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null
      }
    }
  }, [localStream])

  // Setup remote video stream with iOS Safari and mobile-optimized playback
  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      try {
        const videoElement = remoteVideoRef.current
        const videoTracks = remoteStream.getVideoTracks()
        const audioTracks = remoteStream.getAudioTracks()

        // CRITICAL FIX: Validate remote stream tracks
        if (videoTracks.length === 0) {
          console.warn('⚠️ No video tracks in remote stream')
          return
        }

        const videoTrack = videoTracks[0]
        if (videoTrack) {
          console.log('📹 Remote video track settings:', videoTrack.getSettings())

          // MOBILE FIX: Enhanced track validation
          if (videoTrack.readyState !== 'live') {
            console.warn('⚠️ Remote video track not live:', videoTrack.readyState)
            return
          }
        }

        // iOS Safari Detection
        const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

        console.log(`📱 Device detection: iOS Safari: ${isIOSSafari}, Mobile: ${isMobile}`)

        console.log('🔍 REMOTE VIDEO DEBUG:', {
          streamTracks: remoteStream.getTracks().length,
          videoTracks: videoTracks.length,
          audioTracks: audioTracks.length,
          trackSettings: videoTrack?.getSettings(),
          trackConstraints: videoTrack?.getConstraints(),
          elementReadyState: videoElement.readyState
        })

        // iOS Safari and Mobile-optimized configuration
        videoElement.srcObject = remoteStream
        videoElement.playsInline = true
        videoElement.autoplay = true
        videoElement.controls = false
        videoElement.muted = true // CRITICAL: Required for mobile autoplay

        // CRITICAL iOS FIX: Enhanced iOS-specific remote video attributes
        if (isIOSSafari) {
          videoElement.setAttribute('playsinline', 'true') // Standard attribute
          videoElement.setAttribute('webkit-playsinline', 'true') // WebKit prefix
          videoElement.setAttribute('muted', 'true') // Explicit muted for iOS
          videoElement.setAttribute('preload', 'metadata') // iOS optimization
          videoElement.setAttribute('poster', 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K') // Transparent poster to avoid blank screen
          videoElement.style.objectFit = 'cover' // iOS video scaling
          videoElement.style.transform = 'translateZ(0)' // Hardware acceleration
          videoElement.style.willChange = 'transform' // iOS optimization hint
          videoElement.style.webkitTransform = 'translateZ(0)' // WebKit specific
          console.log('📱 iOS Safari enhanced remote video attributes applied')
        }

        // MOBILE ENHANCEMENT: Better mobile compatibility
        videoElement.setAttribute('webkit-playsinline', 'true')
        videoElement.setAttribute('x5-playsinline', 'true') // Tencent X5
        videoElement.setAttribute('x5-video-player-type', 'h5') // Tencent X5 player type
        videoElement.setAttribute('x5-video-player-fullscreen', 'true') // Tencent X5 fullscreen

        // CRITICAL FIX: Enhanced iOS Safari and mobile autoplay handling
        const playRemoteVideo = async () => {
          try {
            // CRITICAL iOS FIX: Enhanced pre-play setup for iOS Safari
            if (isIOSSafari) {
              console.log('📱 iOS Safari: Starting enhanced video initialization')

              // Step 1: Force reload if needed
              if (videoElement.readyState < 1) {
                videoElement.load()
                console.log('📱 iOS Safari: Loading video metadata')

                // Wait for metadata to load with multiple attempts
                let metadataLoaded = false
                for (let attempt = 0; attempt < 3 && !metadataLoaded; attempt++) {
                  await new Promise((resolve) => {
                    const onLoadedMetadata = () => {
                      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata)
                      metadataLoaded = true
                      resolve(true)
                    }
                    videoElement.addEventListener('loadedmetadata', onLoadedMetadata)

                    // Timeout after 2 seconds per attempt
                    setTimeout(() => {
                      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata)
                      resolve(false)
                    }, 2000)
                  })

                  if (!metadataLoaded) {
                    console.warn(`📱 iOS Safari: Metadata load attempt ${attempt + 1} failed, retrying...`)
                    videoElement.load() // Retry loading
                  }
                }
              }

              // Step 2: Ensure video dimensions are available with timeout
              let dimensionAttempts = 0
              while ((videoElement.videoWidth === 0 || videoElement.videoHeight === 0) && dimensionAttempts < 5) {
                console.warn(`📱 iOS Safari: Video dimensions not ready (attempt ${dimensionAttempts + 1}), waiting...`)
                await new Promise(resolve => setTimeout(resolve, 300))
                dimensionAttempts++
              }

              // Step 3: If still no dimensions, force a specific size
              if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
                console.warn('📱 iOS Safari: Forcing video element dimensions')
                videoElement.style.width = '100%'
                videoElement.style.height = '100%'
                videoElement.style.minWidth = '320px'
                videoElement.style.minHeight = '240px'
              }

              console.log('📱 iOS Safari: Video initialization completed')
            }

            await videoElement.play()
            console.log('✅ Remote video playing successfully')

            // EMERGENCY DEBUG: Check iOS video state after play
            if (isIOSSafari) {
              setTimeout(() => {
                console.log('🔍 iOS REMOTE VIDEO STATUS:', {
                  paused: videoElement.paused,
                  ended: videoElement.ended,
                  readyState: videoElement.readyState,
                  videoWidth: videoElement.videoWidth,
                  videoHeight: videoElement.videoHeight,
                  currentTime: videoElement.currentTime,
                  srcObject: !!videoElement.srcObject,
                  style: {
                    width: videoElement.style.width,
                    height: videoElement.style.height,
                    objectFit: videoElement.style.objectFit
                  }
                })
              }, 1000)
            }
          } catch (error) {
            console.warn('⚠️ Remote video autoplay failed:', error)

            // iOS SAFARI FIX: Enhanced error diagnostics
            if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
              console.warn('⚠️ Video element has zero dimensions - likely iOS/mobile constraint issue')

              // iOS SAFARI FIX: Try to force dimensions for iOS
              if (isIOSSafari) {
                videoElement.style.width = '100%'
                videoElement.style.height = '100%'
                console.log('📱 iOS Safari: Forced video dimensions')
              }
            }

            if (isMobile || isIOSSafari) {
              // iOS SAFARI and Mobile-optimized interaction handling
              const promptUserInteraction = () => {
                // Visual indication that tap is needed
                console.log('📱 Prompting mobile/iOS user for video interaction')

                const playOnInteraction = async () => {
                  try {
                    // iOS SAFARI FIX: Additional setup before play
                    if (isIOSSafari) {
                      videoElement.muted = true
                      videoElement.load() // Reload for iOS
                      await new Promise(resolve => setTimeout(resolve, 100))
                    }

                    await videoElement.play()
                    console.log('✅ Remote video playing after mobile/iOS interaction')
                    document.removeEventListener('touchstart', playOnInteraction)
                    document.removeEventListener('click', playOnInteraction)
                    document.removeEventListener('touchend', playOnInteraction)
                  } catch (retryError) {
                    console.error('❌ Mobile/iOS remote video play failed:', retryError)

                    // iOS SAFARI FIX: Final fallback - recreate video element
                    if (isIOSSafari) {
                      console.log('📱 iOS Safari: Attempting video element recreation')
                      try {
                        const newVideoElement = videoElement.cloneNode(true) as HTMLVideoElement
                        newVideoElement.srcObject = remoteStream
                        videoElement.parentNode?.replaceChild(newVideoElement, videoElement)
                        await newVideoElement.play()
                        console.log('✅ iOS Safari: Video playing after element recreation')
                      } catch (finalError) {
                        console.error('❌ iOS Safari: Final video play attempt failed:', finalError)

                        // ULTIMATE FALLBACK: Force video dimensions and try manual reload
                        console.log('📱 iOS Safari: Attempting ultimate fallback')
                        try {
                          videoElement.style.width = '100%'
                          videoElement.style.height = '100%'
                          videoElement.style.objectFit = 'cover'
                          videoElement.load()

                          setTimeout(async () => {
                            try {
                              await videoElement.play()
                              console.log('✅ iOS Safari: Video playing after ultimate fallback')
                            } catch (ultimateError) {
                              console.error('❌ iOS Safari: All video fallbacks failed:', ultimateError)
                            }
                          }, 500)
                        } catch (ultimateError) {
                          console.error('❌ iOS Safari: Ultimate fallback setup failed:', ultimateError)
                        }
                      }
                    }
                  }
                }

                // iOS SAFARI FIX: Enhanced interaction event types
                document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true })
                document.addEventListener('touchend', playOnInteraction, { once: true, passive: true })
                document.addEventListener('click', playOnInteraction, { once: true })

                // iOS SAFARI FIX: Also listen for device orientation changes
                if (isIOSSafari) {
                  window.addEventListener('orientationchange', playOnInteraction, { once: true })
                }

                // Auto-cleanup after 30 seconds
                setTimeout(() => {
                  document.removeEventListener('touchstart', playOnInteraction)
                  document.removeEventListener('touchend', playOnInteraction)
                  document.removeEventListener('click', playOnInteraction)
                  if (isIOSSafari) {
                    window.removeEventListener('orientationchange', playOnInteraction)
                  }
                }, 30000)
              }

              promptUserInteraction()
            } else {
              // Desktop fallback
              setTimeout(async () => {
                try {
                  await videoElement.play()
                  console.log('✅ Remote video playing after desktop retry')
                } catch (retryError) {
                  console.error('❌ Desktop remote video retry failed:', retryError)
                }
              }, 500)
            }
          }
        }

        // Monitor stream quality for mobile
        videoElement.addEventListener('loadedmetadata', () => {
          console.log('📹 Remote video metadata loaded:', {
            videoWidth: videoElement.videoWidth,
            videoHeight: videoElement.videoHeight,
            audioTracks: audioTracks.length,
            videoTracks: videoTracks.length
          })
        })

        // Start playback
        playRemoteVideo()

        console.log('✅ Remote video stream attached with mobile optimization')
      } catch (error) {
        console.error('❌ Failed to attach remote video stream:', error)
      }
    }

    return () => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null
      }
    }
  }, [remoteStream])

  // Handle fullscreen
  const toggleFullscreen = async () => {
    if (!containerRef.current) return

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen()
        setIsFullscreen(true)
      } else {
        await document.exitFullscreen()
        setIsFullscreen(false)
      }
    } catch (error) {
      console.error('Fullscreen error:', error)
    }
  }

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])


  const getLayoutClass = () => {
    switch (layout) {
      case 'grid':
        return 'grid grid-cols-2 gap-4'
      case 'pip':
        return 'relative'
      case 'sidebar':
      default:
        return 'relative'
    }
  }

  const renderNoVideoPlaceholder = (name: string, isLocal: boolean = false) => {
    // AVATAR DEBUG: Check what we're trying to render
    if (!isLocal) {
      console.log(`🖼️ AVATAR DEBUG - VideoCallUI rendering avatar:`)
      console.log(`  callState.activePeerAvatar: "${callState.activePeerAvatar}"`)
      console.log(`  callState.activePeerAvatar type: ${typeof callState.activePeerAvatar}`)
      console.log(`  callState.activePeerAvatar === null: ${callState.activePeerAvatar === null}`)
      console.log(`  callState.activePeerAvatar === 'null': ${callState.activePeerAvatar === 'null'}`)
      console.log(`  name: "${name}"`)
      console.log(`  isLocal: ${isLocal}`)

      // Check what the cleaned avatar value will be
      const cleanedSrc = isLocal ? null : (callState.activePeerAvatar === "null" || callState.activePeerAvatar === null ? null : callState.activePeerAvatar)
      console.log(`  cleanedSrc for Avatar: "${cleanedSrc}"`)
    }

    // Get avatar and fallback data
    const avatarSrc = isLocal ? null : (callState.activePeerAvatar === "null" || callState.activePeerAvatar === null ? null : callState.activePeerAvatar)
    const nameInitial = name.charAt(0).toUpperCase()

    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-800 rounded-lg">
        <div className="mb-4">
          {/* EXACT same as IncomingCallNotification */}
          <div className="relative w-24 h-24 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                className="w-20 h-20 rounded-full object-cover"
              />
            ) : (
              <span className="text-2xl font-semibold text-white">
                {nameInitial}
              </span>
            )}
          </div>
        </div>
      <p className="text-gray-400 text-sm">{name}</p>
      {/* Voice-First Architecture: Enhanced state indicators */}
      {callState.callType === 'voice' && !isLocal && (
        <div className="flex items-center space-x-1 mt-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-green-400">Voice call active</span>
        </div>
      )}
      {isLocal && !isVideoEnabled && callState.callType === 'video' && (
        <div className="flex items-center space-x-1 mt-2">
          <VideoOff className="w-4 h-4 text-gray-500" />
          <span className="text-xs text-gray-500">Camera off</span>
        </div>
      )}
      {isLocal && callState.callType === 'voice' && (
        <div className="flex items-center space-x-1 mt-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-green-400">Audio only</span>
        </div>
      )}
    </div>
    )
  }

  const renderCallProgress = () => {
    if (callState.callProgress === 'idle' || callState.callProgress === 'connected') {
      return null
    }

    const getProgressText = () => {
      switch (callState.callProgress) {
        case 'initializing':
          return 'Initializing'
        case 'connecting':
          return 'Connecting'
        case 'ringing':
          return 'Ringing'
        case 'failed':
          return callState.callError || 'Call failed'
        default:
          return 'Calling'
      }
    }

    const getProgressClass = () => {
      switch (callState.callProgress) {
        case 'failed':
          return 'text-red-400'
        case 'ringing':
          return 'text-blue-400'
        default:
          return 'text-yellow-400'
      }
    }

    return (
      <div className="flex items-center space-x-2">
        <span className={`text-xs sm:text-sm font-medium ${getProgressClass()}`}>
          {getProgressText()}
        </span>
        {callState.callProgress !== 'failed' && (
          <div className={`progress-dots ${getProgressClass()} progress-${callState.callProgress}`}>
            <div className="progress-dot"></div>
            <div className="progress-dot"></div>
            <div className="progress-dot"></div>
          </div>
        )}
      </div>
    )
  }

  const renderVideoQualityOverlay = () => {
    if (!isDegraded && videoQuality !== 'poor' && videoQuality !== 'failed') {
      return null
    }

    const getQualityMessage = () => {
      if (videoQuality === 'failed') {
        return {
          title: 'Video Quality Issue',
          message: 'Video connection failed. Switched to voice-only call.',
          type: 'error'
        }
      } else if (videoQuality === 'poor') {
        return {
          title: 'Poor Video Quality',
          message: 'Connection is unstable. Consider switching to voice-only for better quality.',
          type: 'warning'
        }
      } else if (isDegraded) {
        return {
          title: 'Video Degraded',
          message: 'Video quality reduced due to connection issues.',
          type: 'info'
        }
      }
      return null
    }

    const qualityInfo = getQualityMessage()
    if (!qualityInfo) return null

    const getOverlayClass = () => {
      switch (qualityInfo.type) {
        case 'error':
          return 'bg-red-900/90 border-red-500'
        case 'warning':
          return 'bg-yellow-900/90 border-yellow-500'
        case 'info':
          return 'bg-blue-900/90 border-blue-500'
        default:
          return 'bg-gray-900/90 border-gray-500'
      }
    }

    return (
      <div className={`absolute top-16 left-4 right-4 p-3 rounded-lg border ${getOverlayClass()} text-white z-10`}>
        <div className="flex items-start space-x-3">
          <div className="flex-1">
            <h4 className="font-medium text-sm">{qualityInfo.title}</h4>
            <p className="text-xs mt-1 opacity-90">{qualityInfo.message}</p>
            {videoQuality === 'failed' && callState.callType === 'video' && onUpgradeToVideo && (
              <button
                onClick={onUpgradeToVideo}
                className="mt-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs font-medium transition-colors mobile-touch-target"
              >
                Retry Video
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Voice-First Architecture: Render "Enable Camera" button for voice calls
  const renderVoiceCallCameraButton = () => {
    if (callState.callType !== 'voice' || callState.callProgress !== 'connected') {
      return null
    }

    return (
      <div className="absolute top-16 left-4 right-4 p-3 rounded-lg border bg-blue-900/90 border-blue-500 text-white z-10">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h4 className="font-medium text-sm flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>Voice Call Active</span>
            </h4>
            <p className="text-xs mt-1 opacity-90">Add video to enhance your conversation</p>
          </div>
          {onUpgradeToVideo && (
            <button
              onClick={onUpgradeToVideo}
              className="ml-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors mobile-touch-target flex items-center space-x-2"
              title="Enable camera"
            >
              <Video className="w-4 h-4" />
              <span>Enable Camera</span>
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black z-50 flex flex-col mobile-safe-area"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-2 sm:p-4 z-[55] mobile-safe-area">
        <div className="flex items-center justify-between">
          <div className="flex flex-col space-y-1 min-w-0">
            <h2 className="text-white font-semibold text-base sm:text-lg truncate">
              {/* CRITICAL FIX: Proper alias resolution for video call UI */}
              {callState.activePeerAlias || `User_${callState.activePeer?.slice(-8) || 'Unknown'}`}
            </h2>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {renderCallProgress()}
              {isScreenSharing && (
                <div className="flex items-center space-x-1 px-2 py-1 bg-blue-500/20 rounded-full">
                  <Monitor className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                  <span className="text-2xs sm:text-xs text-blue-400 hidden sm:inline">Screen Sharing</span>
                  <span className="text-2xs text-blue-400 sm:hidden">Screen</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-1 sm:space-x-2">
            {/* Cancel button - Always visible during call setup */}
            {(callState.callProgress === 'initializing' || callState.callProgress === 'connecting' || callState.callProgress === 'ringing') && (
              <button
                onClick={onEndCall}
                className="p-2 bg-red-600/80 hover:bg-red-700/80 rounded-lg text-white transition-colors mobile-touch-target"
                title="Cancel call"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Layout Toggle - Hidden on small screens and during setup */}
            {callState.callProgress === 'connected' && (
              <button
                onClick={() => {
                  const layouts: Array<'grid' | 'sidebar' | 'pip'> = ['sidebar', 'grid', 'pip']
                  const currentIndex = layouts.indexOf(layout)
                  const nextLayout = layouts[(currentIndex + 1) % layouts.length]
                  if (nextLayout) {
                    setLayout(nextLayout)
                  }
                }}
                className="p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-white transition-colors mobile-touch-target hidden sm:flex"
                title="Change layout"
              >
                <Grid className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}

            {/* Pin/Unpin - Hidden on small screens and during setup */}
            {callState.callProgress === 'connected' && (
              <button
                onClick={() => setIsPinned(!isPinned)}
                className="p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-white transition-colors mobile-touch-target hidden sm:flex"
                title={isPinned ? 'Unpin' : 'Pin'}
              >
                {isPinned ? <PinOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Pin className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            )}


            {/* Fullscreen */}
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-white transition-colors mobile-touch-target"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Video Quality Overlay */}
      {renderVideoQualityOverlay()}

      {/* Voice-First Architecture: Enable Camera Button for Voice Calls */}
      {renderVoiceCallCameraButton()}

      {/* CRITICAL FIX: Mobile Video Interaction Prompt */}
      {needsVideoInteraction && (
        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-20 pointer-events-none">
          <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-500/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">Video Ready</h3>
            <p className="text-white/80 text-sm mb-4">{videoInteractionMessage}</p>
            <div className="flex justify-center">
              <div className="w-12 h-12 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      )}

      {/* Video Container */}
      <div className={`flex-1 p-2 sm:p-4 ${getLayoutClass()}`}>
        {layout === 'sidebar' && (
          <>
            {/* Main Video (Remote) */}
            <div className="relative h-full w-full">
              {/* Voice-First Architecture: Handle voice-only calls */}
              {callState.callType === 'voice' ? (
                renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
              ) : remoteStream && !isScreenSharing ? (
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                  className="w-full h-full object-contain lg:object-cover rounded-lg bg-black"
                  aria-label="Remote participant video"
                  key={`remote-video-${layout}`}
                >
                  <track kind="captions" srcLang="en" label="English" default />
                </video>
              ) : isScreenSharing && remoteStream ? (
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                  className="w-full h-full object-contain rounded-lg bg-black"
                  aria-label="Screen share video"
                >
                  <track kind="captions" srcLang="en" label="English" default />
                </video>
              ) : (
                renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
              )}

              {/* Local Video PiP - Desktop-optimized sizing and positioning */}
              <div className="absolute bottom-4 right-4 w-24 h-18 sm:w-32 sm:h-24 md:w-48 md:h-36 lg:w-64 lg:h-48 xl:w-80 xl:h-60 2xl:w-96 2xl:h-72 bg-gray-900 rounded-lg overflow-hidden shadow-2xl z-10 border-2 border-gray-700">
                {/* Voice-First Architecture: Show video only for video calls */}
                {callState.callType === 'video' && localStream && isVideoEnabled ? (
                  <video
                    ref={localVideoRef}
                    autoPlay
                    playsInline
                    muted
                    poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                    className="w-full h-full object-contain md:object-cover"
                    aria-label="Your video"
                    key={`local-video-pip-${layout}`}
                  >
                    <track kind="captions" srcLang="en" label="English" default />
                  </video>
                ) : (
                  renderNoVideoPlaceholder('You', true)
                )}

                {/* Muted Indicator */}
                {isMuted && (
                  <div className="absolute top-1 left-1 sm:top-2 sm:left-2 p-1 bg-red-500 rounded-full">
                    <MicOff className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {layout === 'grid' && (
          <>
            {/* Local Video */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              {/* Voice-First Architecture: Show video only for video calls */}
              {callState.callType === 'video' && localStream && isVideoEnabled ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                  className="w-full h-full object-contain lg:object-cover"
                  aria-label="Your video"
                  key={`local-video-grid-${layout}`}
                >
                  <track kind="captions" srcLang="en" label="English" default />
                </video>
              ) : (
                renderNoVideoPlaceholder('You', true)
              )}

              {isMuted && (
                <div className="absolute top-2 left-2 p-1 bg-red-500 rounded-full">
                  <MicOff className="w-3 h-3 text-white" />
                </div>
              )}

              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                You
              </div>
            </div>

            {/* Remote Video */}
            <div className="relative bg-gray-900 rounded-lg overflow-hidden">
              {/* Voice-First Architecture: Handle voice-only calls in grid layout */}
              {callState.callType === 'voice' ? (
                renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
              ) : remoteStream ? (
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                  className="w-full h-full object-contain lg:object-cover"
                  aria-label="Remote participant video"
                  key={`remote-video-grid-${layout}`}
                >
                  <track kind="captions" srcLang="en" label="English" default />
                </video>
              ) : (
                renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
              )}

              <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-xs">
                {callState.activePeerAlias || 'Remote User'}
              </div>
            </div>
          </>
        )}

        {layout === 'pip' && (
          <>
            {/* Local Video (Main) */}
            <div className="relative h-full w-full">
              {/* Voice-First Architecture: Show video only for video calls in pip layout */}
              {callState.callType === 'video' && localStream && isVideoEnabled ? (
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                  className="w-full h-full object-contain lg:object-cover rounded-lg bg-black"
                  aria-label="Your video"
                  key={`local-video-pip-main-${layout}`}
                >
                  <track kind="captions" srcLang="en" label="English" default />
                </video>
              ) : (
                renderNoVideoPlaceholder('You', true)
              )}

              {/* Remote Video PiP - Desktop-optimized sizing and positioning */}
              <div className="absolute top-4 right-4 w-24 h-18 sm:w-32 sm:h-24 md:w-48 md:h-36 lg:w-64 lg:h-48 xl:w-80 xl:h-60 2xl:w-96 2xl:h-72 bg-gray-900 rounded-lg overflow-hidden shadow-2xl z-10 border-2 border-gray-700">
                {/* Voice-First Architecture: Handle voice-only calls in pip layout */}
                {callState.callType === 'voice' ? (
                  renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
                ) : remoteStream ? (
                  <video
                    ref={remoteVideoRef}
                    autoPlay
                    playsInline
                    muted
                    poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMCAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMDAwMDAwIiBvcGFjaXR5PSIwLjEiLz4KPHN2Zz4K"
                    className="w-full h-full object-contain md:object-cover"
                    aria-label="Remote participant video"
                    key={`remote-video-pip-overlay-${layout}`}
                  >
                    <track kind="captions" srcLang="en" label="English" default />
                  </video>
                ) : (
                  renderNoVideoPlaceholder(callState.activePeerAlias || 'Remote User')
                )}
              </div>

              {isMuted && (
                <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 p-2 bg-red-500 rounded-full">
                  <MicOff className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Call Controls */}
      <CallControls
        callState={callState}
        onEndCall={onEndCall}
        onToggleMute={onToggleMute}
        onToggleVideo={onToggleVideo}
        onToggleScreen={onToggleScreen}
        onToggleFullscreen={toggleFullscreen}
        localStream={localStream || null}
        isMuted={isMuted}
        isVideoEnabled={isVideoEnabled}
        isScreenSharing={isScreenSharing}
        isFullscreen={isFullscreen}
        connectionQuality={connectionQuality}
      />

    </div>
  )
}